import { FC, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import $api from "../../shared/api/axios";
import { TPromptLint, TRevisionEntry, hasLintErrors } from "./revision";
import { PromptFlagBadge } from "./PromptFlagBadge";

type TProps = {
  featureKey: string;
  revisionId: number;
  entry: TRevisionEntry;
  onSaved: () => void;
};

const LINT_DEBOUNCE_MS = 400;

export const PromptEditor: FC<TProps> = ({ featureKey, revisionId, entry, onSaved }) => {
  const [content, setContent] = useState(entry.content);
  const [lint, setLint] = useState<TPromptLint>(entry.lint);
  const [isActiveOpen, setIsActiveOpen] = useState(false);

  useEffect(() => {
    setContent(entry.content);
    setLint(entry.lint);
  }, [entry.content, entry.lint]);

  useEffect(() => {
    if (content === entry.content) return undefined;

    const timer = setTimeout(async () => {
      try {
        const response = await $api.post(`/admin/prompts/features/${featureKey}/prompts/${entry.promptKey}/lint`, {
          content
        });
        setLint(response.data);
      } catch {
        /* empty */
      }
    }, LINT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [content, entry.content, entry.promptKey, featureKey]);

  const save = useMutation({
    mutationFn: () => $api.patch(`/admin/prompts/revisions/${revisionId}/entries/${entry.promptKey}`, { content }),
    onSuccess: () => {
      toast.success(`Saved ${entry.promptKey}`);
      onSaved();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to save the prompt")
  });

  const reset = useMutation({
    mutationFn: () => $api.post(`/admin/prompts/revisions/${revisionId}/entries/${entry.promptKey}/reset`),
    onSuccess: () => {
      toast.success(`Reset ${entry.promptKey} to the active revision`);
      onSaved();
    },
    onError: () => toast.error("Failed to reset the prompt")
  });

  const isDirty = content !== entry.content;
  const blocked = hasLintErrors(lint);

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[14px] font-medium text-gray-900">{entry.label}</div>
          <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-0.5">
            {entry.promptKey}
          </FigmaTypography>
          {entry.description && (
            <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
              {entry.description}
            </FigmaTypography>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <PromptFlagBadge featureFlag={entry.featureFlag} />
          {entry.differsFromActive && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-[12px] leading-[18px] text-amber-800">
              Differs from active revision
            </span>
          )}
        </div>
      </div>

      <div className="px-6 py-4 space-y-3">
        {entry.requiredPlaceholders.length > 0 && (
          <div className="text-[12px] leading-[18px] text-gray-500">
            <span className="font-semibold">Required:</span>{" "}
            {entry.requiredPlaceholders.map((placeholder) => (
              <span
                key={placeholder}
                className={`mx-0.5 rounded px-1.5 py-0.5 font-mono text-[12px] ${
                  content.includes(placeholder) ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-800"
                }`}
              >
                {placeholder}
              </span>
            ))}
          </div>
        )}

        <textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          spellCheck={false}
          className={`h-[420px] w-full resize-y rounded border px-3 py-2 font-mono text-[12px] leading-[18px] text-gray-800 outline-none focus:ring-1 ${
            blocked ? "border-red-300 focus:ring-red-300" : "border-gray-200 focus:ring-gray-300"
          }`}
        />

        {blocked && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[12px] leading-[18px] text-red-800">
            {lint.missingRequired.length > 0 && <div>Missing required: {lint.missingRequired.join(", ")}</div>}
            {lint.unknown.length > 0 && <div>No caller supplies: {lint.unknown.join(", ")}</div>}
          </div>
        )}

        <div className="flex items-center gap-2">
          <AppButton
            onClick={() => save.mutate()}
            disabled={!isDirty || blocked}
            isLoading={save.isPending}
            loadingText="Saving..."
          >
            Save
          </AppButton>
          <AppButton
            appearance="outlined"
            onClick={() => reset.mutate()}
            disabled={!entry.differsFromActive && !isDirty}
            isLoading={reset.isPending}
            loadingText="Resetting..."
          >
            Reset to active revision
          </AppButton>
          {isDirty && (
            <AppButton appearance="text" onClick={() => setContent(entry.content)}>
              Discard changes
            </AppButton>
          )}
          <button
            type="button"
            onClick={() => setIsActiveOpen(!isActiveOpen)}
            className="ml-auto text-[12px] leading-[18px] text-gray-500 hover:text-gray-700"
          >
            {isActiveOpen ? "Hide active revision" : "Show active revision"}
          </button>
        </div>

        {isActiveOpen && (
          <pre className="max-h-[420px] overflow-auto rounded border border-gray-200 bg-gray-50 px-3 py-2 whitespace-pre-wrap font-mono text-[12px] leading-[18px] text-gray-500">
            {entry.activeContent}
          </pre>
        )}
      </div>
    </div>
  );
};
